import { useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Report } from "@/types";
import { reportService } from "@/services/reportService";
import { FileText, AlertCircle, Calendar, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { UserAvatar } from "@/components/UserAvatar";
import { useQuery } from "@tanstack/react-query";

interface PriorityReportsListProps {
  priorityPid: string;
  enabled: boolean;
  onCountChange?: (count: number) => void;
}

export function PriorityReportsList({
  priorityPid,
  enabled,
  onCountChange,
}: PriorityReportsListProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "priority", priorityPid],
    queryFn: () => reportService.getReports(1, 100, { priorityPid }),
    enabled,
  });

  const reports: Report[] = data?.data || [];

  useEffect(() => {
    onCountChange?.(reports.length);
  }, [reports.length, onCountChange]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-3 w-12" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-sm text-muted-foreground">Failed to load reports</p>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="font-semibold mb-2">No Reports Yet</h3>
        <p className="text-sm text-muted-foreground">
          No reports have been submitted for this priority.
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-3 pr-4 pb-2">
        {reports.map((report) => (
          <div
            key={report.pid}
            className="rounded-lg border border-border bg-card p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserAvatar
                  src={report.user?.avatarUrl}
                  name={report.user?.fullName}
                  className="h-8 w-8"
                />
                <div>
                  <div className="font-medium text-sm">
                    {report.user?.fullName}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {format(new Date(report.reportDate), "MMM d, yyyy")}
                    </span>
                  </div>
                </div>
              </div>
              {report.blocker && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Blocker
                </Badge>
              )}
            </div>

            <div className="text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1.5">
                <TrendingUp className="h-3 w-3" />
                <span className="text-xs font-medium uppercase tracking-wide">Update</span>
              </div>
              <div className="whitespace-pre-wrap leading-relaxed">{report.content}</div>
            </div>

            {report.blocker && report.blockedReason && (
              <div className="text-sm rounded-md bg-destructive/5 border border-destructive/20 p-3">
                <div className="text-destructive font-medium mb-1">
                  Blocker Details:
                </div>
                <div className="text-muted-foreground whitespace-pre-wrap">
                  {report.blockedReason}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
